import {
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { SignatureData, SignoffRole, SignoffState } from 'types'

import { SignaturePad } from './SignaturePad'

const { Option } = Select
const { Title, Text } = Typography

const SignoffCard = styled(Card)`
  margin-bottom: 1rem;
`

const LockBanner = styled.div`
  background: ${(props) => props.theme.forecast.warning}15;
  border: 1px solid ${(props) => props.theme.forecast.warning};
  border-radius: 0.25rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const QualificationOptions = [
  'UTT Level II',
  'UTDA Level II',
  'ASNT Level III Ultrasonics',
  'Advanced Services Manager',
  'NDT Technology Manager',
]

interface SignoffSectionProps {
  currentSignoff?: SignoffState
  onSignoff: (signatureData: SignatureData) => Promise<void>
  onGeneratePDF?: () => Promise<void>
  loading?: boolean
  isLocked: boolean
  onStartSignoff: () => Promise<void>
  onUnlock: () => Promise<void>
  publishedPdfPath?: string
  metadataEntryId?: string
  canLock?: boolean | undefined
  isPublished?: boolean
}

export const SignoffSection: React.FC<SignoffSectionProps> = ({
  currentSignoff,
  onSignoff,
  loading = false,
  isLocked,
  onStartSignoff,
  onUnlock,
  canLock = true,
  isPublished = false,
}) => {
  const [role, setRole] = useState<SignoffRole>()
  const [form] = Form.useForm()
  const [tempSignature, setTempSignature] = useState<string>()
  const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw')

  const handleStartSignoff = useCallback(async () => {
    Modal.confirm({
      title: 'Start Sign-off Process',
      content:
        'Starting the sign-off process will lock the report for editing. You will need to explicitly unlock it to make any changes, which will invalidate any existing signatures.',
      icon: <LockOutlined />,
      onOk: async () => {
        try {
          await onStartSignoff()
          message.success('Report locked for sign-off')
        } catch {
          message.error('Failed to lock report')
        }
      },
    })
  }, [onStartSignoff])

  const handleUnlock = useCallback(async () => {
    Modal.confirm({
      title: 'Unlock Report',
      content:
        'Unlocking the report will invalidate all existing signatures. Are you sure you want to continue?',
      icon: <WarningOutlined />,
      okText: 'Unlock and Clear Signatures',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await onUnlock()
          setRole(undefined)
          setTempSignature(undefined)
          form.resetFields()
          message.success('Report unlocked and signatures cleared')
        } catch {
          message.error('Failed to unlock report')
        }
      },
    })
  }, [onUnlock, form])

  const handleSubmitSignature = useCallback(
    async (signatureData: SignatureData) => {
      try {
        await onSignoff(signatureData)
        setRole(undefined)
        setTempSignature(undefined)
        form.resetFields()
      } catch {
        message.error('Failed to process signoff')
      }
    },
    [form, onSignoff]
  )

  const handleRoleChange = useCallback(
    (value: SignoffRole) => {
      if (!isLocked) {
        message.warning(
          'Please lock the report before starting the sign-off process'
        )
        setRole(undefined)
        form.setFieldValue('role', undefined)
        return
      }

      form.resetFields()
      setRole(value)

      form.setFieldsValue({ role: value })

      if (
        value === SignoffRole.SUPERVISOR &&
        currentSignoff?.supervisorSignoff
      ) {
        form.setFieldsValue({
          name: currentSignoff.supervisorSignoff.name,
          email: currentSignoff.supervisorSignoff.email,
          qualifications: currentSignoff.supervisorSignoff.qualifications,
        })
      } else if (
        value === SignoffRole.REVIEWER &&
        currentSignoff?.reviewerSignoff
      ) {
        form.setFieldsValue({
          name: currentSignoff.reviewerSignoff.name,
          email: currentSignoff.reviewerSignoff.email,
          qualifications: currentSignoff.reviewerSignoff.qualifications,
        })
      } else if (
        value === SignoffRole.DATA_ANALYST &&
        currentSignoff?.dataAnalystSignoff
      ) {
        form.setFieldsValue({
          name: currentSignoff.dataAnalystSignoff.name,
          email: currentSignoff.dataAnalystSignoff.email,
          qualifications: currentSignoff.dataAnalystSignoff.qualifications,
        })
      }
    },
    [currentSignoff, form, isLocked]
  )

  useEffect(() => {
    setTempSignature(undefined)
    setSignatureMode('draw')
  }, [role])

  const handleSignature = useCallback((signature: string) => {
    setTempSignature(signature)
  }, [])

  const handleSignatureUpload = useCallback(
    (file: RcFile) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('Please upload an image file (PNG, JPG, etc.)')
        return false
      }

      const isWithinSizeLimit = file.size / 1024 / 1024 < 2
      if (!isWithinSizeLimit) {
        message.error('Image must be smaller than 2MB')
        return false
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const result = e.target?.result as string
        if (result) {
          setTempSignature(result)
        }
      }
      reader.onerror = () => {
        message.error('Failed to read image file')
      }
      reader.readAsDataURL(file)

      return false // Prevent default upload behavior
    },
    [setTempSignature]
  )

  const handleSubmit = useCallback(async () => {
    if (!tempSignature) return

    try {
      const values = await form.validateFields()
      const signatureData: SignatureData = {
        role: role!,
        name: values.name,
        email: values.email,
        qualifications: values.qualifications,
        timestamp: new Date().toISOString(),
        signature: tempSignature, // This is base64 string
      }

      await handleSubmitSignature(signatureData)
    } catch {
      message.error('Please fill in all required fields')
    }
  }, [form, role, tempSignature, handleSubmitSignature])

  return (
    <SignoffCard
      title={
        <Space>
          <Title level={4}>Report Signoff</Title>
          {isLocked && (
            <Tag color='error' icon={<LockOutlined />}>
              Locked for Sign-off
            </Tag>
          )}
        </Space>
      }>
      {!isLocked &&
        (!currentSignoff?.reviewerSignoff ||
          !currentSignoff?.supervisorSignoff ||
          !currentSignoff?.dataAnalystSignoff) && (
          <LockBanner>
            <Text>
              <WarningOutlined />{' '}
              {canLock
                ? 'Lock the report to begin the sign-off process'
                : 'Report cannot be locked while required sections are missing'}
            </Text>
            <Button
              type='primary'
              icon={<LockOutlined />}
              onClick={handleStartSignoff}
              disabled={loading || !canLock}>
              Lock and Start Sign-off
            </Button>
          </LockBanner>
        )}

      {isLocked && (
        <>
          <Button
            danger
            icon={<UnlockOutlined />}
            onClick={handleUnlock}
            disabled={loading || isPublished}
            style={{ marginBottom: '1rem' }}>
            Unlock Report
          </Button>
          {isPublished && (
            <Text>
              <WarningOutlined /> A published report cannot be unlocked. Please
              create a new revision.
            </Text>
          )}
        </>
      )}

      {(!currentSignoff?.reviewerSignoff ||
        !currentSignoff?.supervisorSignoff ||
        !currentSignoff?.dataAnalystSignoff) &&
        isLocked && (
          <Form form={form} layout='vertical'>
            <Form.Item
              name='role'
              label='Select Role'
              rules={[{ required: true }]}>
              <Select
                value={role}
                onChange={handleRoleChange}
                disabled={loading || isPublished}
                placeholder='Select your role'>
                {!currentSignoff?.supervisorSignoff && (
                  <Option value={SignoffRole.SUPERVISOR}>
                    Inspection Supervisor
                  </Option>
                )}
                {!currentSignoff?.dataAnalystSignoff && (
                  <Option value={SignoffRole.DATA_ANALYST}>Data Analyst</Option>
                )}
                {!currentSignoff?.reviewerSignoff && (
                  <Option value={SignoffRole.REVIEWER}>Reviewer</Option>
                )}
              </Select>
            </Form.Item>

            {role && (
              <>
                <Form.Item
                  name='name'
                  label='Full Name'
                  rules={[{ required: true }]}>
                  <Input disabled={loading} />
                </Form.Item>

                <Form.Item
                  name='email'
                  label='Email'
                  rules={[{ required: true, type: 'email' }]}>
                  <Input disabled={loading} />
                </Form.Item>

                <Form.Item
                  name='qualifications'
                  label='Qualifications'
                  rules={[{ required: true }]}>
                  <Select
                    options={QualificationOptions.map((val) => ({
                      value: val,
                      label: val,
                    }))}
                    disabled={loading}
                  />
                </Form.Item>

                {!tempSignature ? (
                  <SignatureInputContainer>
                    <Segmented
                      options={[
                        {
                          label: (
                            <Space>
                              <EditOutlined />
                              Draw Signature
                            </Space>
                          ),
                          value: 'draw',
                        },
                        {
                          label: (
                            <Space>
                              <UploadOutlined />
                              Upload Image
                            </Space>
                          ),
                          value: 'upload',
                        },
                      ]}
                      value={signatureMode}
                      onChange={(value) =>
                        setSignatureMode(value as 'draw' | 'upload')
                      }
                      disabled={loading}
                      style={{ marginBottom: '1rem' }}
                    />

                    {signatureMode === 'draw' ? (
                      <SignaturePad
                        onSign={handleSignature}
                        disabled={loading}
                      />
                    ) : (
                      <UploadContainer>
                        <Upload.Dragger
                          accept='image/*'
                          showUploadList={false}
                          beforeUpload={handleSignatureUpload}
                          disabled={loading}>
                          <UploadContent>
                            <UploadOutlined style={{ fontSize: '2rem' }} />
                            <p>Click or drag an image of your signature here</p>
                            <UploadHint>
                              Supports PNG, JPG, and other image formats (max
                              2MB)
                            </UploadHint>
                          </UploadContent>
                        </Upload.Dragger>
                      </UploadContainer>
                    )}
                  </SignatureInputContainer>
                ) : (
                  <SignaturePreview>
                    <img
                      src={tempSignature}
                      alt='Signature Preview'
                      style={{
                        maxWidth: '25rem',
                        background: 'white',
                        padding: '0.625rem',
                        borderRadius: '0.25rem',
                      }}
                    />
                    <Space style={{ marginTop: '1rem' }}>
                      <Button onClick={() => setTempSignature(undefined)}>
                        Redo Signature
                      </Button>
                      <Button
                        type='primary'
                        onClick={handleSubmit}
                        loading={loading}>
                        {role === SignoffRole.REVIEWER
                          ? 'Submit Review'
                          : role === SignoffRole.DATA_ANALYST
                            ? 'Submit Data Analyst Signature'
                            : 'Submit Signature'}
                      </Button>
                    </Space>
                  </SignaturePreview>
                )}
              </>
            )}
          </Form>
        )}

      {currentSignoff?.supervisorSignoff && (
        <SignatureDisplay>
          <Title level={5}>Supervisor Signoff</Title>
          <Text>Name: {currentSignoff.supervisorSignoff.name}</Text>
          <br />
          <Text>
            Qualifications: {currentSignoff.supervisorSignoff.qualifications}
          </Text>
          <br />
          <Text>Email: {currentSignoff.supervisorSignoff.email}</Text>
          <br />
          <Text>
            Date:{' '}
            {new Date(
              currentSignoff.supervisorSignoff.timestamp
            ).toLocaleString()}
          </Text>
          <br />
          <img
            src={currentSignoff.supervisorSignoff.signature}
            alt='Supervisor Signature'
            style={{
              maxWidth: '25rem',
              background: 'white',
              padding: '0.625rem',
              borderRadius: '0.25rem',
              marginTop: '0.625rem',
            }}
          />
        </SignatureDisplay>
      )}

      {currentSignoff?.dataAnalystSignoff && (
        <SignatureDisplay>
          <Title level={5}>Data Analyst Signoff</Title>
          <Text>Name: {currentSignoff.dataAnalystSignoff.name}</Text>
          <br />
          <Text>
            Qualifications: {currentSignoff.dataAnalystSignoff.qualifications}
          </Text>
          <br />
          <Text>Email: {currentSignoff.dataAnalystSignoff.email}</Text>
          <br />
          <Text>
            Date:{' '}
            {new Date(
              currentSignoff.dataAnalystSignoff.timestamp
            ).toLocaleString()}
          </Text>
          <br />
          <img
            src={currentSignoff.dataAnalystSignoff.signature}
            alt='Data Analyst Signature'
            style={{
              maxWidth: '25rem',
              background: 'white',
              padding: '0.625rem',
              borderRadius: '0.25rem',
              marginTop: '0.625rem',
            }}
          />
        </SignatureDisplay>
      )}

      {currentSignoff?.reviewerSignoff && (
        <SignatureDisplay>
          <Title level={5}>Reviewer Signoff</Title>
          <Text>Name: {currentSignoff.reviewerSignoff.name}</Text>
          <br />
          <Text>
            Qualifications: {currentSignoff.reviewerSignoff.qualifications}
          </Text>
          <br />
          <Text>Email: {currentSignoff.reviewerSignoff.email}</Text>
          <br />
          <Text>
            Date:{' '}
            {new Date(
              currentSignoff.reviewerSignoff.timestamp
            ).toLocaleString()}
          </Text>
          <br />
          <img
            src={currentSignoff.reviewerSignoff.signature}
            alt='Reviewer Signature'
            style={{
              maxWidth: '25rem',
              background: 'white',
              padding: '0.625rem',
              borderRadius: '0.25rem',
              marginTop: '0.625rem',
            }}
          />
        </SignatureDisplay>
      )}
    </SignoffCard>
  )
}

const SignatureDisplay = styled.div`
  background: #1f1f1f;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
`

const SignaturePreview = styled.div`
  margin-top: 1rem;
`

const SignatureInputContainer = styled.div`
  margin-top: 0.5rem;
`

const UploadContainer = styled.div`
  width: 25rem;

  .ant-upload-drag {
    background: #1f1f1f;
    border-color: #434343;

    &:hover {
      border-color: #177ddc;
    }
  }
`

const UploadContent = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);

  p {
    margin-top: 0.75rem;
    margin-bottom: 0.25rem;
  }
`

const UploadHint = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
`
